import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Phone, Mail, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Page /boutique-suspendue
 *
 * Affichée quand l'abonnement de la boutique est suspendu (manuellement ou date
 * de fin dépassée) : le backend répond SHOP_SUSPENDED sur toute l'API et
 * l'intercepteur redirige ici. /subscription/status reste joignable pendant la
 * suspension, il fournit le message et les coordonnées choisis par le compte racine.
 */
function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* presse-papiers indisponible */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      aria-label="Copier"
      title="Copier"
      className="h-12 w-12 shrink-0 flex items-center justify-center rounded-md border border-stone text-graphite hover:bg-sand transition-colors"
    >
      {copied ? <Check className="h-5 w-5 text-copper" /> : <Copy className="h-5 w-5" />}
    </button>
  );
}

export default function ShopSuspended() {
  const [status, setStatus] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/subscription/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ blocked: true }));
  }, []);

  if (!status) return <div className="min-h-screen bg-cream" />;

  if (!status.blocked) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="shop-suspended-page">
        <div className="max-w-md w-full bg-white border border-stone rounded-lg p-8 text-center">
          <h1 className="font-serif text-2xl text-ink mb-3">La boutique est accessible</h1>
          <Link to="/" className="inline-block mt-2 bg-copper text-white px-6 py-3 rounded-md text-sm">Retour à la boutique</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10" data-testid="shop-suspended-page">
      <div className="max-w-xl w-full bg-white border border-stone rounded-lg px-6 py-10 sm:px-10 sm:py-12 text-center">
        <div className="mx-auto h-20 w-20 rounded-full bg-sand flex items-center justify-center mb-6">
          <Lock className="h-9 w-9 text-copper" strokeWidth={1.75} />
        </div>
        <h1 className="text-2xl sm:text-4xl font-semibold text-ink tracking-tight">
          {status.title || "Accès à la boutique suspendu"}
        </h1>
        {status.shop_name && (
          <p className="mt-2 text-graphite">
            Boutique : <span className="font-medium text-ink">{status.shop_name}</span>
          </p>
        )}
        {status.message && (
          <p className="mt-6 text-graphite leading-relaxed whitespace-pre-line">{status.message}</p>
        )}

        {(status.phone || status.email) && (
          <div className="mt-8 space-y-3 text-left">
            {status.phone && (
              <div className="flex items-center gap-3">
                <a
                  href={`tel:${status.phone.replace(/\s/g, "")}`}
                  data-testid="shop-suspended-phone"
                  className="flex-1 min-w-0 h-14 flex items-center justify-center gap-3 rounded-md bg-copper text-white font-semibold text-base sm:text-lg hover:opacity-90 transition-opacity"
                >
                  <Phone className="h-5 w-5 shrink-0" />
                  <span className="truncate">{status.phone}</span>
                </a>
                <CopyButton value={status.phone} testId="shop-suspended-copy-phone" />
              </div>
            )}
            {status.email && (
              <div className="flex items-center gap-3">
                <a
                  href={`mailto:${status.email}`}
                  data-testid="shop-suspended-email"
                  className="flex-1 min-w-0 h-14 flex items-center justify-center gap-3 rounded-md border border-stone text-ink font-medium text-base sm:text-lg hover:bg-sand transition-colors"
                >
                  <Mail className="h-5 w-5 shrink-0" />
                  <span className="truncate">{status.email}</span>
                </a>
                <CopyButton value={status.email} testId="shop-suspended-copy-email" />
              </div>
            )}
          </div>
        )}

        {user?.role === "superadmin" && (
          <Link to="/admin/abonnement" data-testid="shop-suspended-manage" className="inline-block mt-8 text-sm text-copper underline underline-offset-4 hover:text-ink">
            Gérer l&apos;abonnement
          </Link>
        )}
      </div>
    </div>
  );
}
